import { useState, useEffect, useCallback } from 'react';
import { SITE_ID, SITE_COLOR_SCHEME_DEFAULT } from '@/config/site';
import {
  COLOR_SCHEME_EVENT,
  applyColorScheme,
  isColorScheme,
  readCachedScheme,
  resolveInitialScheme,
} from '@/theme/color-scheme';
import type { ColorScheme } from '@/theme/color-scheme';

type Theme = ColorScheme;

const STORAGE_KEY = `${SITE_ID}-theme`;

function getStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isColorScheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * The best site default known before settings arrive: the scheme the last
 * load actually applied (cached by RuntimeBrand / this hook), else the
 * build-time baseline. 'system' resolves through prefers-color-scheme inside
 * resolveInitialScheme.
 */
function getSiteDefault(): string {
  return readCachedScheme() ?? SITE_COLOR_SCHEME_DEFAULT;
}

/**
 * The visitor's colour scheme. Their own toggle choice is stored per tenant
 * and always wins; otherwise the site's default applies (see
 * web/src/theme/color-scheme.ts for the precedence). RuntimeBrand applies the
 * LIVE default when settings load and broadcasts it, so a mounted header
 * toggle shows the right icon without owning that logic.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() =>
    resolveInitialScheme(getStoredTheme(), getSiteDefault()),
  );

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch {
      /* storage unavailable — the choice lasts for this page only */
    }
    applyColorScheme(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  // Apply theme class on mount
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Follow a site-default change applied elsewhere (RuntimeBrand, when the
  // live color_scheme_default arrives and the visitor has no stored choice).
  useEffect(() => {
    const onScheme = (e: Event) => {
      const next = (e as CustomEvent<ColorScheme>).detail;
      if (isColorScheme(next) && !getStoredTheme()) setThemeState(next);
    };
    window.addEventListener(COLOR_SCHEME_EVENT, onScheme);
    return () => window.removeEventListener(COLOR_SCHEME_EVENT, onScheme);
  }, []);

  return { theme, setTheme, toggleTheme };
}
